import type { GetDraftAssessmentActivitiesQuery } from '../assessment-builder.graphql.generated';
import AssessmentBuilderIntroductionPrint from '../introduction/assessment-builder-intro-print';
import { ActivityBuilderThemeContextProvider } from '../../theme/activity-builder-theme';
import { INTRODUCTION_INDEX, Orientation } from '../../activity-builder.apollo';
import { groupSections } from '../../print/activity-builder-a4.utils';
import * as Styled from '../../print/activity-builder-a4.style';
import { GlobalStyles } from '../../print/activity-builder-print-generator.style';
import type { InteractiveLessonSectionFragment } from '../../lesson-builder.graphql.generated';
import ActivityBuilderPrintGeneratorPage from 'approot/activity-builder/print/activity-builder-print-generator-page';
import { LanguageCodes } from 'global/constants';
import type { Pluck } from 'lib/gql/pluck';

export const AssessmentBuilderPrintGenerator = ({
  interactiveAssessment,
  sections,
  slidesPerPage,
  orientation,
  slides,
  version,
  language,
  onRenderComplete,
}: {
  interactiveAssessment: Pluck<
    GetDraftAssessmentActivitiesQuery,
    'getDraftAssessmentActivities'
  >;
  sections: InteractiveLessonSectionFragment[];
  slidesPerPage: number;
  orientation: Orientation;
  slides: number[];
  version: string | undefined;
  language: LanguageCodes;
  onRenderComplete?: (totalPages: number) => void;
}) => {
  const { sortedSections, totalPages } = groupSections({
    sections,
    slidesToDisplay: slides,
    slidesPerPage,
    isSinglePage: false,
    currentPage: undefined,
  });

  const assessment = interactiveAssessment.assessment;
  const showCover = slides.includes(INTRODUCTION_INDEX);

  return (
    <ActivityBuilderThemeContextProvider
      initialFontSize={interactiveAssessment.styles?.fontSize}
    >
      <GlobalStyles $language={language}>
        <Styled.A4 $orientation={orientation}>
          {showCover && (
            <Styled.A4Page $orientation={orientation}>
              <AssessmentBuilderIntroductionPrint
                interactiveAssessment={interactiveAssessment}
                language={language}
              />
            </Styled.A4Page>
          )}
          {sortedSections.map((group, index) => {
            return (
              <Styled.A4Page $orientation={orientation} key={index}>
                <Styled.A4Slides>
                  {group.map(section => {
                    return (
                      <ActivityBuilderPrintGeneratorPage
                        key={section.id}
                        language={language}
                        includesIntro={showCover}
                        index={index}
                        section={section}
                        version={version}
                        slidesPerPage={slidesPerPage}
                        orientation={orientation}
                        unitName={assessment.unit.name}
                        contentId={interactiveAssessment.id}
                        contentName={assessment.name}
                        subject={assessment.subjects?.[0]?.label}
                        sectionsOverview={
                          interactiveAssessment?.sectionsOverview
                        }
                        onRenderComplete={() => onRenderComplete?.(totalPages)}
                      />
                    );
                  })}
                </Styled.A4Slides>
              </Styled.A4Page>
            );
          })}
        </Styled.A4>
      </GlobalStyles>
    </ActivityBuilderThemeContextProvider>
  );
};
